import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  UrlRunComparisonService,
  type CrawlRunIssueComparison,
  type CrawlRunIssueEntry,
  type CrawlRunMetricDelta,
  type CrawlRunRef,
  type RunComparisonMetricGroup,
  type UrlRunComparisonPayload
} from '../../services/url-run-comparison.service';

/**
 * Run comparison on URL Detail — FW-CRAWL-DELTA-01 phase 3.
 * Spec: docs/features/MONITORING_CRAWL_DELTAS.md §6 phase 3.
 *
 * **Its own component for its own stylesheet.** `url-detail.css` minifies to
 * roughly 45 bytes over the 90 KB `anyComponentStyle` error budget in
 * `angular.json`, so the production build already fails there
 * (TECHNICAL_DEBT §0t). The budget is per component: one rule added to that file
 * makes it worse, and one added here costs nothing.
 *
 * Every number on screen comes from `delta.display`, which the backend owns.
 * The component never subtracts and never formats a number itself — if it did,
 * "not compared" would eventually render as `0` somewhere, which is precisely
 * what §4 says makes the feature untrustworthy.
 */

interface MetricGroupView {
  group: RunComparisonMetricGroup;
  label: string;
  metrics: CrawlRunMetricDelta[];
}

const GROUP_LABELS: Array<{ group: RunComparisonMetricGroup; label: string }> = [
  { group: 'demand', label: 'Demand' },
  { group: 'serp', label: 'SERP' },
  { group: 'technical', label: 'Technical' },
  { group: 'freshness', label: 'Freshness' }
];

@Component({
  selector: 'app-url-run-comparison',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './url-run-comparison.html',
  styleUrl: './url-run-comparison.css'
})
export class UrlRunComparisonComponent implements OnChanges {
  @Input() urlId: string | null = null;

  loading = false;
  error: string | null = null;
  payload: UrlRunComparisonPayload | null = null;

  constructor(private runComparison: UrlRunComparisonService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['urlId']) this.load();
  }

  private load(): void {
    const urlId = (this.urlId || '').trim();
    this.payload = null;
    this.error = null;
    if (!urlId) return;

    this.loading = true;
    this.runComparison.getForUrl(urlId).subscribe({
      next: (response) => {
        this.payload = response?.data ?? null;
        this.loading = false;
      },
      error: () => {
        this.error = 'Run comparison could not be loaded.';
        this.loading = false;
      }
    });
  }

  hasRuns(): boolean {
    return !!this.payload && this.payload.runsCaptured > 0;
  }

  comparison() {
    return this.payload?.comparison ?? null;
  }

  /** Grouped for reading order; empty groups are dropped rather than shown blank. */
  metricGroups(): MetricGroupView[] {
    const metrics = this.comparison()?.metrics ?? [];
    return GROUP_LABELS.map(({ group, label }) => ({
      group,
      label,
      metrics: metrics.filter((m) => m.group === group)
    })).filter((g) => g.metrics.length > 0);
  }

  /** Only a compared metric may be coloured — a suppressed one is grey on purpose. */
  metricStateClass(metric: CrawlRunMetricDelta): string {
    if (metric.state !== 'compared') return 'run-metric--suppressed';
    if (metric.improved === true) return 'run-metric--improved';
    if (metric.improved === false) return 'run-metric--regressed';
    return metric.changed ? 'run-metric--moved' : 'run-metric--flat';
  }

  runLabel(run: CrawlRunRef | null): string {
    if (!run) return 'none';
    const day = (run.completedAt || '').slice(0, 10);
    return day || run.crawlRunId;
  }

  runQualifier(run: CrawlRunRef | null): string | null {
    if (!run) return null;
    const bits: string[] = [];
    if (run.backfilled) bits.push('backfilled');
    if (run.crawlMode) bits.push(run.crawlMode);
    if (typeof run.urlsCaptured === 'number') bits.push(`${run.urlsCaptured} URLs`);
    return bits.length ? bits.join(' · ') : null;
  }

  // --- Issues resolved vs new (phase 4) -------------------------------------

  /** Null when there is no comparison at all; otherwise always present, and
   *  possibly suppressed — which the template must render as prose. */
  issues(): CrawlRunIssueComparison | null {
    return this.comparison()?.issues ?? null;
  }

  /** Whether the lists may be shown. Anything else renders `display` instead:
   *  an empty `resolved` list under a "Resolved" heading reads as "nothing was
   *  fixed", and an empty `appeared` list reads as "nothing broke" — both are
   *  claims the backend explicitly refused to make. */
  hasIssueLists(): boolean {
    const issues = this.issues();
    return !!issues && issues.state === 'compared';
  }

  /** True when the issue list moved. A page whose issues are unchanged says so
   *  once, rather than showing two empty lists. */
  issuesMoved(): boolean {
    const issues = this.issues();
    if (!issues || issues.state !== 'compared') return false;
    return issues.resolvedCount > 0 || issues.appearedCount > 0 || issues.reclassifiedCount > 0;
  }

  /** The backend leaves `label` null when a run stored fingerprints without the
   *  strings behind them. Say so rather than rendering a blank bullet. */
  issueLabel(entry: { label: string | null; fingerprint: string }): string {
    return entry.label ?? 'Unnamed issue — this run stored no issue text';
  }

  /** Only shown on a persisting issue: the counts DataForSEO writes into the
   *  text are normalised out of the fingerprint, so this is where they surface. */
  issueReworded(entry: CrawlRunIssueEntry): string | null {
    return entry.previousLabel ? `was: ${entry.previousLabel}` : null;
  }

  /** "captured" / "not captured" — never a blank cell, so a missing row reads as
   *  a fact about the run rather than as an empty slot. */
  historyRowLabel(entry: { snapshot: { analysisOk: boolean; analysisNote: string | null } | null }): string {
    if (!entry.snapshot) return 'not in this run';
    return entry.snapshot.analysisOk ? 'captured' : `capture failed${entry.snapshot.analysisNote ? ` — ${entry.snapshot.analysisNote}` : ''}`;
  }
}
